import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    FaArrowLeft, FaUserCircle, FaEnvelope, FaIdCard,
    FaUserTag, FaPhone, FaBuilding, FaBed, FaClock,
    FaCheckCircle, FaTimesCircle, FaEdit
} from 'react-icons/fa';
import '../styles/ViewProfile.css';

const API_BASE_URL =
    process.env.REACT_APP_API_URL ||
    (process.env.NODE_ENV === 'production'
        ? 'https://kanang-alalay-backend.onrender.com/api'
        : 'http://localhost:5000/api');

const ViewProfile = () => {
    const { user: ctxUser, logout } = useAuth();
    const navigate = useNavigate();

    // Keep a live reference to ctxUser for the catch-block fallback below,
    // without making the fetch effect depend on it (see note by the
    // useEffect for why that matters).
    const ctxUserRef = useRef(ctxUser);
    useEffect(() => { ctxUserRef.current = ctxUser; }, [ctxUser]);

    // Back always returns through the user's dashboard (not raw browser
    // history) so it can never leave the app or land on a blank page.
    // replace:true removes this page from history entirely, so the
    // browser's forward/back buttons can't bring the user back to it.
    const goBack = () => navigate(ctxUser?.role === 'admin' ? '/admin' : '/head-caregiver', { replace: true });
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error,   setError]   = useState('');

    // Fetch fresh profile from backend.
    //
    // Deliberately runs ONCE on mount ([] deps), not on every ctxUser
    // change, to avoid unnecessary refetching. ctxUserRef (set up above)
    // gives the catch-block fallback fresh data without needing ctxUser
    // in the dependency array.
    useEffect(() => {
        const fetchProfile = async () => {
            const token = localStorage.getItem('token');
            try {
                const res  = await fetch(`${API_BASE_URL}/auth/profile`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const data = await res.json();
                if (data.success) {
                    setProfile(data.user);
                }
                else setError('Failed to load profile.');
            } catch {
                // Fallback to context user
                setProfile(ctxUserRef.current);
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const u = profile || ctxUser;

    const SHIFT_LABELS = {
        morning:   'Morning (6AM–2PM)',
        afternoon: 'Afternoon (2PM–10PM)',
        night:     'Night (10PM–6AM)',
        flexible:  'Flexible',
        rotating:  'Rotating',
    };

    const InfoBox = ({ icon, label, value }) => (
        <div className="info-box">
            <div className="info-icon">{icon}</div>
            <div className="info-text">
                <label>{label}</label>
                <p>{value || <span className="not-provided">Not provided</span>}</p>
            </div>
        </div>
    );

    if (loading) return (
        <div className="page-wrapper">
            <div className="content-container">
                <div style={{ textAlign:'center', padding:'60px 0', color:'#7A5C4E' }}>Loading profile…</div>
            </div>
        </div>
    );

    return (
        <div className="page-wrapper">
            <div className="content-container">
                <div className="page-header">
                    <button className="back-btn" onClick={goBack}>
                        <FaArrowLeft /> Back
                    </button>
                    <h2>My Profile</h2>
                </div>

                {error && <div className="profile-error-banner">{error}</div>}

                <div className="profile-card">
                    {/* Top section */}
                    <div className="profile-top">
                        <div className="profile-avatar-col">
                            <div className="profile-avatar-wrap">
                                <FaUserCircle className="profile-avatar" />
                                <span className={`profile-status-dot ${u?.isActive ? 'online' : 'offline'}`} title={u?.isActive ? 'Active' : 'Inactive'} />
                            </div>
                        </div>
                        <div className="profile-title">
                            <h1>{u?.firstName} {u?.lastName || ''}</h1>
                            <span className="role-badge">{u?.role?.toUpperCase() || 'STAFF'}</span>
                            <div className="profile-status-text">
                                {u?.isActive
                                    ? <><FaCheckCircle style={{color:'#1E7D56',marginRight:6}}/>Account Active</>
                                    : <><FaTimesCircle style={{color:'#C0392B',marginRight:6}}/>Account Inactive</>
                                }
                            </div>
                        </div>
                    </div>

                    {/* Info grid */}
                    <div className="profile-grid">
                        <InfoBox icon={<FaIdCard />}    label="Staff ID"        value={u?.staffId} />
                        <InfoBox icon={<FaUserTag />}   label="Username"        value={u?.username ? `@${u.username}` : null} />
                        <InfoBox icon={<FaEnvelope />}  label="Email Address"   value={u?.email} />
                        <InfoBox icon={<FaPhone />}     label="Contact Number"  value={u?.phone} />
                        {(u?.role === 'nurse' || u?.role === 'caregiver') && (
                            <>
                                <InfoBox icon={<FaBuilding />} label="Floor / Ward" value={u?.ward} />
                                <InfoBox icon={<FaClock />}    label="Shift"        value={SHIFT_LABELS[u?.shift] || u?.shift} />
                            </>
                        )}
                        {u?.department && (
                            <InfoBox icon={<FaBuilding />} label="Department" value={u?.department} />
                        )}
                    </div>

                    {/* Member since */}
                    {u?.createdAt && (
                        <div className="profile-member-since">
                            Member since {new Date(u.createdAt).toLocaleDateString('en-PH', { year:'numeric', month:'long', day:'numeric' })}
                        </div>
                    )}

                    <div className="profile-footer">
                        <button className="brand-btn" onClick={() => navigate('/settings')}>
                            <FaEdit /> Edit Settings
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewProfile;